import { trackError, trackEvent } from '@vp/om-newrelic-tracker';
import cartSelectors from '../state/cart/selectors';
import { CART_NEWRELIC_BROWSER_AGENT_IDENTIFIER } from '../constants/newRelic';

/**
 *  PageAction Name for this application.
 */
const CART_UI_ERROR_TYPE = 'cart-ui-error';
const CART_UI_EVENT_TYPE = 'cart-ui-event';
/**
 * Errors being tracked
 */
const VISTACART_API_SUBTYPE = 'VISTACART_API';
const CART_LOAD_FAIL = `${VISTACART_API_SUBTYPE}.CART_LOAD_FAIL`;
const CART_ITEM_REMOVE = `${VISTACART_API_SUBTYPE}.CART_ITEM_REMOVE`;
const CART_ITEM_GROUP_REMOVE = `${VISTACART_API_SUBTYPE}.CART_ITEM_GROUP_REMOVE`;
const CART_ITEM_QTY_UPDATE = `${VISTACART_API_SUBTYPE}.CART_ITEM_QTY_UPDATE`;
const DISCOUNT_CODE_SAVE_FAIL = `${VISTACART_API_SUBTYPE}.DISCOUNT_CODE_SAVE_FAIL`;
const DISCOUNT_CODE_CLEAR_FAIL = `${VISTACART_API_SUBTYPE}.DISCOUNT_CODE_CLEAR_FAIL`;
const CHECKOUT_DISABLED = `${VISTACART_API_SUBTYPE}.PROCEED_TO_CHECKOUT_DISABLED`;
const PENDING_OPERATIONS_RELOADS_MAX = `${VISTACART_API_SUBTYPE}.PENDING_OPERATIONS_RELOADS_MAX`;
const PENDING_OPERATIONS_RELOAD = `${VISTACART_API_SUBTYPE}.PENDING_OPERATIONS_RELOAD`;

const PRICING_CONTEXT_NOT_AVAILABLE = `PRICING_CONTEXT_API.NOT_AVAILABLE`;

const SAVE_FOR_LATER_CLICKED = `CARTUI.SAVE_FOR_LATER_CLICKED`;
const SAVE_FOR_LATER_CANCELLED = `CARTUI.SAVE_FOR_LATER_CANCELLED`;
const SAVE_FOR_LATER_CONFIRMED = `CARTUI.SAVE_FOR_LATER_CONFIRMED`;

/**
 * Format error tracking data suitable for viewing (and querying) in New Relic.
 * @param {string} errorReason queriable error type describing where the error took place.
 * @param {*} errorObj Optional error object passed from exception.
 */
function createCustomErrorData(errorReason: string, errorObj: {} = {}) {
  return {
    errorReason,
    error: JSON.stringify(errorObj),
  };
}

const trackEventWrapper = (actionName: string, eventData: any) => {
  const debug = process.env.NODE_ENV !== 'production';
  trackEvent(
    CART_NEWRELIC_BROWSER_AGENT_IDENTIFIER,
    actionName,
    eventData,
    debug
  );
};

export const noticeError = (error: unknown, errorData: any = {}) => {
  const debug = process.env.NODE_ENV !== 'production';
  trackError(
    CART_NEWRELIC_BROWSER_AGENT_IDENTIFIER,
    error as Error,
    errorData,
    debug
  );
};

export function trackCheckoutBlockedForCustomerCart(cart: any) {
  const { invalidValidityChecks } = {
    invalidValidityChecks: cartSelectors.selectInvalidValidityChecks(cart),
  };
  trackEventWrapper(
    CART_UI_ERROR_TYPE,
    createCustomErrorData(CHECKOUT_DISABLED, {
      invalidValidityChecks,
    })
  );
}

export function trackCartLoadError(errorData: {}) {
  trackEventWrapper(
    CART_UI_ERROR_TYPE,
    createCustomErrorData(CART_LOAD_FAIL, errorData)
  );
}

export function trackDiscountCodeSaveError(
  errorData: {},
  newCoupon: string,
  couponErrorMessageKey?: string
) {
  trackEventWrapper(CART_UI_ERROR_TYPE, {
    newCoupon,
    couponErrorMessageKey,
    ...createCustomErrorData(DISCOUNT_CODE_SAVE_FAIL, errorData),
  });
}

export function trackDiscountCodeClearError(errorData: {}) {
  trackEventWrapper(
    CART_UI_ERROR_TYPE,
    createCustomErrorData(DISCOUNT_CODE_CLEAR_FAIL, errorData)
  );
}

export function trackCartItemRemoveError(errorData: {}) {
  trackEventWrapper(
    CART_UI_ERROR_TYPE,
    createCustomErrorData(CART_ITEM_REMOVE, errorData)
  );
}

export function trackCartItemGroupRemoveError(errorData: {}) {
  trackEventWrapper(
    CART_UI_ERROR_TYPE,
    createCustomErrorData(CART_ITEM_GROUP_REMOVE, errorData)
  );
}

export function trackCartItemQuantityUpdateError(errorData: {}) {
  trackEventWrapper(
    CART_UI_ERROR_TYPE,
    createCustomErrorData(CART_ITEM_QTY_UPDATE, errorData)
  );
}

export function trackPricingContextNotAvailable(errorData: {}) {
  trackEventWrapper(
    CART_UI_ERROR_TYPE,
    createCustomErrorData(PRICING_CONTEXT_NOT_AVAILABLE, errorData)
  );
}

export function trackPendingOperationsDrivenCartReloadAttempts(errorData: {}) {
  trackEventWrapper(
    CART_UI_EVENT_TYPE,
    createCustomErrorData(PENDING_OPERATIONS_RELOAD, errorData)
  );
}

export function trackMaxPendingOperationsDrivenCartReloadAttempts(errorData: {}) {
  trackEventWrapper(
    CART_UI_ERROR_TYPE,
    createCustomErrorData(PENDING_OPERATIONS_RELOADS_MAX, errorData)
  );
}

export function trackSaveForLaterClicks(cartItemId: string) {
  trackEventWrapper(SAVE_FOR_LATER_CONFIRMED, {
    cartItemId,
  });
}

export function trackOpenSaveForLaterClicks(cartItemId: string) {
  trackEventWrapper(SAVE_FOR_LATER_CLICKED, {
    cartItemId,
  });
}

export function trackCancelSaveForLaterClicks(cartItemId: string) {
  trackEventWrapper(SAVE_FOR_LATER_CANCELLED, {
    cartItemId,
  });
}
